/**
 * GPSSpeedDial - Location Controls with Personal & Crew Sharing
 * 
 * Business Logic:
 * - INDIVIDUAL PLAN: Full control - can turn GPS on/off anytime
 * - CREW MEMBER: If you joined someone else's crew, GPS sharing is MANDATORY
 *   - Cannot turn off crew sharing while part of a crew
 *   - Only way to stop sharing is to LEAVE the crew
 * - CREW OWNER: Full control over their own GPS
 * 
 * Two mini-FABs that pop out at bottom of screen:
 * 1. Personal Location - Required for app navigation (requests actual GPS)
 * 2. Crew Sharing - Shows status, locked if member of another crew
 * 
 * Visual Status Indicator:
 * - Green pulse = Location active
 * - Yellow = Pending/requesting
 * - Red = Denied/off
 * - Amber lock = Crew member (cannot disable)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import GpsOffIcon from '@mui/icons-material/GpsOff';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import LocationOffIcon from '@mui/icons-material/LocationOff';
import GroupsIcon from '@mui/icons-material/Groups';
import GroupRemoveIcon from '@mui/icons-material/GroupRemove';
import LockIcon from '@mui/icons-material/Lock';
import { shouldAskPermission } from './LocationPermissionDialog';
import { telegram } from '../services/telegram';
import { feedbackService } from '../services/feedback';
import { getCyberColors } from '../theme/cyberColors';
import { requestPreciseLocation, storeExternalFix } from '../services/highPrecisionLocation';

// Zone type for popout direction (kept for interface compatibility)
type FabZone = 'top' | 'bottom' | 'left' | 'right';

interface GPSSpeedDialProps {
  darkMode: boolean;
  highContrast?: boolean;
  zone?: FabZone;
  // Personal location state
  personalLocationEnabled: boolean;
  onTogglePersonalLocation: () => void;
  onRequestLocation: () => Promise<void>;
  // Crew sharing state
  crewSharingEnabled: boolean;
  onToggleCrewSharing: () => void;
  // Location status
  locationStatus: 'active' | 'pending' | 'denied' | 'off';
  // Crew membership info - determines if user can toggle crew sharing
  isCrewMember?: boolean;      // Is the user a member of ANY crew (not owner)
  isCrewOwner?: boolean;       // Is the user an owner of a crew
  crewName?: string;           // Name of crew they're a member of
  // Styling
  fabSize?: number;
  iconSize?: number;
  borderWidth?: number;
}

// Status color mapping
const getStatusColor = (status: 'active' | 'pending' | 'denied' | 'off'): string => {
  switch (status) {
    case 'active': return '#10b981'; // Green
    case 'pending': return '#f59e0b'; // Yellow/Orange
    case 'denied': return '#ef4444'; // Red
    case 'off': return '#6b7280'; // Gray
  }
};

export function GPSSpeedDial({
  darkMode,
  highContrast = false,
  zone: _zone = 'bottom', // Kept for interface compatibility
  personalLocationEnabled,
  onTogglePersonalLocation,
  onRequestLocation,
  crewSharingEnabled,
  onToggleCrewSharing,
  locationStatus,
  isCrewMember = false,
  isCrewOwner = false,
  crewName,
  fabSize = 60,
  iconSize = 28,
  borderWidth = 5,
}: GPSSpeedDialProps) {
  const [open, setOpen] = useState(false);
  const cyber = getCyberColors(darkMode, highContrast);
  void cyber; // Used for future styling consistency
  const statusColor = getStatusColor(locationStatus);
  
  // Auto-hide timer ref
  const autoHideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Auto-hide mini FABs after 5 seconds of inactivity
  useEffect(() => {
    if (open) {
      // Clear any existing timer
      if (autoHideTimerRef.current) {
        clearTimeout(autoHideTimerRef.current);
      }
      // Set new auto-hide timer
      autoHideTimerRef.current = setTimeout(() => {
        setOpen(false);
      }, 5000); // 5 seconds
    }
    
    return () => {
      if (autoHideTimerRef.current) {
        clearTimeout(autoHideTimerRef.current);
      }
    };
  }, [open]);

  // Determine if crew sharing toggle is locked (mandatory for non-owner crew members)
  const isCrewSharingLocked = isCrewMember && !isCrewOwner;

  // Handle personal location toggle with actual GPS request
  const handlePersonalLocation = async () => {
    feedbackService.tap();
    telegram.hapticFeedback('medium');
    
    if (!personalLocationEnabled) {
      // Turning ON - request actual location
      await onRequestLocation();
    }
    onTogglePersonalLocation();
    setOpen(false);
  };

  // Handle crew sharing toggle with crew member restriction
  const handleCrewSharing = () => {
    feedbackService.tap();
    
    // If user is a crew member but NOT the owner, they cannot turn off crew sharing
    if (isCrewSharingLocked && crewSharingEnabled) {
      telegram.hapticFeedback('error');
      telegram.showPopup({
        title: '🔒 Location Sharing Required',
        message: crewName 
          ? `As a member of "${crewName}", your location must be shared with your crew for safety coordination. Only crew owners can modify this setting.`
          : 'As a crew member, your location must be shared with your crew for safety coordination. Only crew owners can modify this setting.',
        buttons: [{ id: 'ok', type: 'ok', text: 'Understood' }]
      });
      setOpen(false);
      return;
    }
    
    telegram.hapticFeedback(crewSharingEnabled ? 'light' : 'success');
    onToggleCrewSharing();
    setOpen(false);
  };

  // Determine main icon based on state
  const getMainIcon = () => {
    if (personalLocationEnabled && crewSharingEnabled) {
      return <GpsFixedIcon sx={{ fontSize: iconSize, color: cyber.neonGreen }} />;
    } else if (personalLocationEnabled) {
      return <MyLocationIcon sx={{ fontSize: iconSize, color: cyber.neonCyan }} />;
    } else {
      return <GpsOffIcon sx={{ fontSize: iconSize, color: cyber.neonRed }} />;
    }
  };

  // Actions for the speed dial
  const actions = [
    {
      id: 'personal',
      icon: personalLocationEnabled 
        ? <MyLocationIcon sx={{ fontSize: 24, color: '#10b981' }} />
        : <LocationOffIcon sx={{ fontSize: 24, color: '#ef4444' }} />,
      name: personalLocationEnabled ? 'Location ON' : 'Location OFF',
      subtitle: 'Required for navigation',
      onClick: handlePersonalLocation,
      enabled: personalLocationEnabled,
      color: personalLocationEnabled ? '#10b981' : '#ef4444',
      locked: false,
    },
    {
      id: 'crew',
      icon: isCrewSharingLocked ? (
        // Locked state - show lock icon overlay
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <GroupsIcon sx={{ fontSize: 24, color: '#f59e0b' }} />
          <LockIcon sx={{ 
            position: 'absolute', 
            fontSize: 12, 
            color: '#ef4444',
            bottom: -4,
            right: -4,
            backgroundColor: 'rgba(0,0,0,0.8)',
            borderRadius: '50%',
            padding: '2px'
          }} />
        </Box>
      ) : crewSharingEnabled
        ? <GroupsIcon sx={{ fontSize: 24, color: '#f59e0b' }} />
        : <GroupRemoveIcon sx={{ fontSize: 24, color: '#6b7280' }} />,
      name: isCrewSharingLocked 
        ? '🔒 Sharing Required' 
        : crewSharingEnabled ? 'Crew Sharing ON' : 'Crew Sharing OFF',
      subtitle: isCrewSharingLocked 
        ? `Locked by ${crewName || 'crew'} membership`
        : 'Share with your crew',
      onClick: handleCrewSharing,
      enabled: crewSharingEnabled,
      color: isCrewSharingLocked ? '#f59e0b' : crewSharingEnabled ? '#f59e0b' : '#6b7280',
      locked: isCrewSharingLocked,
    },
  ];

  return (
    <>
      <Box sx={{ position: 'relative', width: fabSize, height: fabSize }}>
        {/* Status indicator dot */}
        <Box
          sx={{
            position: 'absolute',
            top: -4,
            right: 4,
            width: 14,
            height: 14,
            borderRadius: '50%',
            backgroundColor: statusColor,
            border: `2px solid ${darkMode ? '#1e293b' : '#fff'}`,
            zIndex: 10,
            // Pulse animation for active state
            ...(locationStatus === 'active' && {
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%': { boxShadow: `0 0 0 0 ${statusColor}80` },
                '70%': { boxShadow: `0 0 0 8px ${statusColor}00` },
                '100%': { boxShadow: `0 0 0 0 ${statusColor}00` },
              },
            }),
            // Blink animation for pending
            ...(locationStatus === 'pending' && {
              animation: 'blink 1s infinite',
              '@keyframes blink': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.4 },
              },
            }),
          }}
        />

        {/* Main FAB */}
        <Tooltip title={personalLocationEnabled && crewSharingEnabled ? 'GPS & Crew Active' : personalLocationEnabled ? 'GPS Active' : 'GPS Controls'} placement="top">
          <IconButton
            onClick={() => {
              setOpen(!open);
              feedbackService.tap();
              telegram.hapticFeedback('light');
            }}
            sx={{
              width: fabSize,
              height: fabSize,
              backgroundColor: darkMode ? 'rgba(30, 41, 59, 0.95)' : 'rgba(255, 255, 255, 0.95)',
              border: `${borderWidth}px solid ${statusColor}`,
              boxShadow: `0 0 20px ${statusColor}40`,
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: darkMode ? 'rgba(51, 65, 85, 0.95)' : 'rgba(241, 245, 249, 0.95)',
                boxShadow: `0 0 30px ${statusColor}60`,
              },
              '&:active': {
                transform: 'scale(0.95)',
              },
            }}
          >
            {getMainIcon()}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Mini FABs - Fixed position at bottom of screen */}
      {open && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 100,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 1.5,
            zIndex: 1300,
            animation: 'slideUp 0.2s ease-out',
            '@keyframes slideUp': {
              from: { opacity: 0, transform: 'translateX(-50%) translateY(20px)' },
              to: { opacity: 1, transform: 'translateX(-50%) translateY(0)' },
            },
          }}
        >
          {actions.map((action, index) => (
            <Tooltip 
              key={action.id} 
              title={
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>{action.name}</Typography>
                  <Typography sx={{ fontSize: '0.7rem', opacity: 0.8 }}>{action.subtitle}</Typography>
                </Box>
              } 
              arrow 
              placement="top"
            >
              <IconButton
                onClick={() => {
                  action.onClick();
                  setOpen(false);
                }}
                sx={{
                  width: 52,
                  height: 52,
                  background: `linear-gradient(135deg, ${action.color}20 0%, ${action.color}40 100%)`,
                  border: `3px solid ${action.color}`,
                  boxShadow: `0 0 15px ${action.color}60`,
                  animation: `popIn 0.2s ease-out ${index * 0.1}s both`,
                  '@keyframes popIn': {
                    from: { opacity: 0, transform: 'scale(0.5)' },
                    to: { opacity: 1, transform: 'scale(1)' },
                  },
                  '&:hover': {
                    transform: 'scale(1.1)',
                    boxShadow: `0 0 25px ${action.color}80`,
                  },
                }}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>
      )}

      {/* Backdrop when open */}
      {open && (
        <Box
          onClick={() => setOpen(false)}
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1299,
          }}
        />
      )}
    </>
  );
}

// ============================================================================
// Hook for managing GPS state
// ============================================================================

interface GPSState {
  personalLocationEnabled: boolean;
  crewSharingEnabled: boolean;
  locationStatus: 'active' | 'pending' | 'denied' | 'off';
  currentLocation: { lat: number; lng: number } | null;
  // Crew membership info for GPS toggle locking
  isCrewMember: boolean;       // Is user a member of ANY crew (not owner)
  isCrewOwner: boolean;        // Is user an owner of a crew
  crewName: string | null;     // Name of crew they're a member of (for display)
}

export function useGPSState() {
  const [state, setState] = useState<GPSState>(() => ({
    // Default to true if not explicitly set to 'false' in localStorage
    personalLocationEnabled: localStorage.getItem('personal-location-enabled') !== 'false',
    crewSharingEnabled: localStorage.getItem('crew-location-sharing') === 'true',
    locationStatus: 'off',
    currentLocation: null,
    isCrewMember: false,
    isCrewOwner: false,
    crewName: null,
  }));

  // Fetch crew membership info on mount
  useEffect(() => {
    async function fetchCrewMembership() {
      try {
        // Dynamic import to avoid circular dependencies
        const { crewService } = await import('../services/crewService');
        const { crews } = await crewService.getMyCrews();
        
        if (crews.length > 0) {
          // Find if user is owner or member
          // user_role is populated by the API for each crew
          const ownedCrew = crews.find(c => c.user_role === 'owner');
          const memberCrew = crews.find(c => c.user_role === 'member' || c.user_role === 'admin');
          
          setState(prev => ({
            ...prev,
            isCrewOwner: !!ownedCrew,
            isCrewMember: !!memberCrew,
            crewName: memberCrew?.name || null,
            // If user is a crew member (not owner), force crew sharing ON
            crewSharingEnabled: memberCrew ? true : prev.crewSharingEnabled,
          }));
          
          // If user is a member of someone else's crew, also save to localStorage
          if (memberCrew) {
            localStorage.setItem('crew-location-sharing', 'true');
          }
        }
      } catch (error) {
        console.log('[GPS] Failed to fetch crew membership:', error);
      }
    }
    
    fetchCrewMembership();
  }, []);

  // Update localStorage when state changes
  useEffect(() => {
    localStorage.setItem('personal-location-enabled', state.personalLocationEnabled.toString());
    localStorage.setItem('crew-location-sharing', state.crewSharingEnabled.toString());
  }, [state.personalLocationEnabled, state.crewSharingEnabled]);

  // Internal function that actually requests location from browser geolocation
  // NOTE: Skipping Telegram's requestLocation() because it shows an ugly popup
  const actuallyRequestLocation = useCallback(async (): Promise<{ lat: number; lng: number } | null> => {
    setState(prev => ({ ...prev, locationStatus: 'pending' }));

    const result = await requestPreciseLocation({
      desiredAccuracyMeters: 25,
      refinementTimeoutMs: 6000,
      allowLastKnownFallback: false,
      allowTelegramFallback: false,
    });

    if (!result.fix) {
      const reason = result.reason?.toLowerCase() || '';
      setState(prev => ({
        ...prev,
        locationStatus: reason.includes('denied') ? 'denied' : 'off',
      }));
      return null;
    }

    storeExternalFix(result.fix);

    const location = { lat: result.fix.lat, lng: result.fix.lng };
    setState(prev => ({
      ...prev,
      locationStatus: 'active',
      currentLocation: location,
      personalLocationEnabled: true,
    }));
    return location;
  }, []);

  // Request actual GPS location
  // Returns location if allowed, null if denied, or 'needs-permission' if dialog should be shown
  const requestLocation = useCallback(async (
    options?: { showPermissionDialog?: (onGranted: () => void, onDenied: () => void, feature?: string) => void; feature?: string }
  ): Promise<{ lat: number; lng: number } | null> => {
    // First, check our stored permission preference
    const { shouldAsk, storedChoice } = shouldAskPermission();
    
    // If permission has expired or was never granted, ask again
    if (storedChoice === 'ask' || shouldAsk) {
      // If we need to ask and caller provided a dialog callback, show dialog first
      if (options?.showPermissionDialog) {
        return new Promise((resolve) => {
          options.showPermissionDialog!(
            async () => {
              // Permission granted via our dialog - now actually request location
              const location = await actuallyRequestLocation();
              resolve(location);
            },
            () => {
              // Permission denied via our dialog
              setState(prev => ({ ...prev, locationStatus: 'denied' }));
              resolve(null);
            },
            options?.feature
          );
        });
      }
    }
    
    // If we don't need to ask, request location directly
    return actuallyRequestLocation();
  }, [actuallyRequestLocation, shouldAskPermission]);

  // Toggle personal location
  const togglePersonalLocation = useCallback(() => {
    setState(prev => {
      const newEnabled = !prev.personalLocationEnabled;
      return {
        ...prev,
        personalLocationEnabled: newEnabled,
        locationStatus: newEnabled ? prev.locationStatus : 'off',
      };
    });
  }, []);

  // Toggle crew sharing - blocked if user is a crew member (not owner)
  const toggleCrewSharing = useCallback(() => {
    setState(prev => {
      // If user is a crew member but NOT owner, and trying to turn OFF - block it
      if (prev.isCrewMember && !prev.isCrewOwner && prev.crewSharingEnabled) {
        // Show popup message (handled in component, not here)
        return prev; // No change
      }
      return {
        ...prev,
        crewSharingEnabled: !prev.crewSharingEnabled,
      };
    });
  }, []);

  // Watch position when personal location is enabled
  useEffect(() => {
    if (!state.personalLocationEnabled) return;

    let watchId: number | null = null;

    if (navigator.geolocation) {
      watchId = navigator.geolocation.watchPosition(
        (position) => {
          storeExternalFix({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy:
              typeof position.coords.accuracy === 'number'
                ? Math.max(position.coords.accuracy, 0)
                : null,
            timestamp: position.timestamp,
            source: 'browser-watch',
          });
          setState(prev => ({
            ...prev,
            locationStatus: 'active',
            currentLocation: {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            },
          }));
        },
        (error) => {
          if (error.code === 1) {
            setState(prev => ({ ...prev, locationStatus: 'denied' }));
          }
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 5000 }
      );
    }

    return () => {
      if (watchId !== null) {
        navigator.geolocation.clearWatch(watchId);
      }
    };
  }, [state.personalLocationEnabled]);

  return {
    ...state,
    requestLocation,
    togglePersonalLocation,
    toggleCrewSharing,
  };
}
